/**
 * This file exclusively contains helper functions to get metric values FROM main grid
 */
import { GridApi } from "ag-grid-community";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { IGridParamsQueryParam } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels";
import { IAllocationDimensionValue } from "helpers/allocation-dimension";
import { IDoor, filterDatesOrDoors, getAttributeFilters, getDimensionFilters } from "helpers/door-collection";
import { generateMetricColId } from "helpers/metrics/column-formula/ag-grid-helper";
import { MetricsKind, Metric } from "helpers/metrics/types";
import memoizeOne from "memoize-one";
import { metricsValueGetterCreator } from "../getter-setter-factory";

// TODO: write unit tests

// Helper to get grand total metric column name
// @param metricKind - metric kind
export const getTotalMetricColumnName = (metricKind: MetricsKind) => {
    const totalMetricColumnName = generateMetricColId(ColumnTypes.AllDimensionTotal, metricKind);

    return totalMetricColumnName;
};

interface IGetRequiredMetricOptions {
    gridParams: IGridParamsQueryParam;
    deliveries: IAllocationDimensionValue[];
    doors: IDoor[];
}
export const getRequiredMetricParams = memoizeOne(
    (options: IGetRequiredMetricOptions) => {
        const { gridParams, deliveries, doors } = options;
        const dimensionFilters = getDimensionFilters(gridParams);
        const attributeFilters = getAttributeFilters(gridParams);

        const filteredDeliveries = filterDatesOrDoors(deliveries, "dates", dimensionFilters, deliveries.length > 0);
        const filteredDoors = filterDatesOrDoors(
            doors,
            "doors",
            dimensionFilters,
            deliveries.length > 0,
            attributeFilters
        );

        // Generate delivery and door sets to optimize performance for horizontal formulas
        const filteredDeliveriesForAssortmentSet = new Set<string>(
            filteredDeliveries.map((dimensionValue) => dimensionValue.id)
        );
        const filteredDoorsForAssortmentSet = new Map<string, IDoor>(filteredDoors.map((door) => [door.id, door]));

        return {
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet,
        };
    },
    (newArgs, oldArgs) => {
        // Shallow-compare options' keys from newArgs and oldArgs
        return Object.keys(newArgs[0]).every((key) => newArgs[0][key] === oldArgs[0][key]);
    }
);

export const getMetricValueGetterFn = memoizeOne(
    (options: {
        rootGridApi: GridApi;
        metric: Metric;
        filteredDeliveriesForAssortmentSet: Set<string>;
        filteredDoorsForAssortmentSet: Map<string, IDoor>;
        columnDimensionIds: string[][];
    }) => {
        const {
            metric,
            rootGridApi,
            columnDimensionIds,
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet,
        } = options;

        const deliveryId = columnDimensionIds && columnDimensionIds[0].length === 1 ? columnDimensionIds[0][0] : null;
        const metricDoors = columnDimensionIds
            ? columnDimensionIds[1].reduce((acc, doorId) => {
                  const door = filteredDoorsForAssortmentSet.get(doorId);
                  if (door) {
                      acc.push(door);
                  }
                  return acc;
              }, [])
            : [];

        // This is the required options to calculate metric
        return metricsValueGetterCreator({
            metric,
            deliveryId,
            doors: metricDoors,
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet,
            useSize: false,
            rootGridApi,
        });
    },
    (newArgs, oldArgs) => {
        return (
            newArgs[0].metric.kind === oldArgs[0].metric.kind &&
            newArgs[0].columnDimensionIds === oldArgs[0].columnDimensionIds
        );
    }
);
